/**
 * Página de Parte de Avería para conductores
 * Permite reportar averías en autobuses y ver partes recientes del mismo vehículo
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../context/ToastContext';
import { averias as averiasApi } from '../services/api';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import {
    ArrowLeft,
    Wrench,
    Bus,
    Calendar,
    FileText,
    Send,
    AlertCircle,
    Clock,
    CheckCircle,
    Pencil,
    Trash2,
    X
} from 'lucide-react';

import { useAuth } from '../context/AuthContext';
import ConfirmModal from '../components/ConfirmModal';

export default function Averia() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const toast = useToast();

    // Form state
    const [fecha, setFecha] = useState(() => {
        const today = new Date();
        return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    });
    const [numAutobus, setNumAutobus] = useState('');
    const [descripcion, setDescripcion] = useState('');
    const [editingId, setEditingId] = useState(null); // ID of report being edited
    const [submitting, setSubmitting] = useState(false);

    // Delete validation modal
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [deleteTargetId, setDeleteTargetId] = useState(null);

    // Recent reports for the same bus
    const [recentReports, setRecentReports] = useState([]);
    const [loadingRecent, setLoadingRecent] = useState(false);

    // Debounce timer for bus number input
    const [debounceTimer, setDebounceTimer] = useState(null);

    // Load recent reports when bus number changes
    useEffect(() => {
        if (debounceTimer) clearTimeout(debounceTimer);

        if (numAutobus.trim().length >= 2) {
            const timer = setTimeout(() => {
                loadRecentReports(numAutobus.trim());
            }, 500);
            setDebounceTimer(timer);
        } else {
            setRecentReports([]);
        }

        return () => {
            if (debounceTimer) clearTimeout(debounceTimer);
        };
    }, [numAutobus]);

    const loadRecentReports = async (busNum) => {
        try {
            setLoadingRecent(true);
            const data = await averiasApi.getRecientes(busNum);
            setRecentReports(data || []);
        } catch (error) {
            console.warn('Error loading recent reports:', error);
            setRecentReports([]);
        } finally {
            setLoadingRecent(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!numAutobus.trim()) {
            toast.error('Introduce el número de autobús');
            return;
        }
        if (!descripcion.trim()) {
            toast.error('Describe la avería');
            return;
        }

        setSubmitting(true);
        try {
            if (editingId) {
                // Update existing report
                await averiasApi.update(editingId, {
                    fecha,
                    num_autobus: numAutobus.trim(),
                    descripcion: descripcion.trim()
                });
                toast.success('Parte actualizado correctamente');
                setEditingId(null);
            } else {
                // Create new report
                await averiasApi.crear({
                    fecha,
                    num_autobus: numAutobus.trim(),
                    descripcion: descripcion.trim()
                });
                toast.success('Parte de avería enviado correctamente');
            }

            // Reset description but keep date/bus if creating multiple
            setDescripcion('');
            if (editingId) {
                // Initial state reset if we were editing
                const today = new Date();
                setFecha(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`);
                setNumAutobus('');
            }

            // Reload recent reports
            loadRecentReports(numAutobus.trim());
        } catch (error) {
            toast.error(error.message || 'Error al guardar el parte');
        } finally {
            setSubmitting(false);
        }
    };

    const handleEdit = (report) => {
        setEditingId(report.id);
        setFecha(report.fecha);
        setNumAutobus(report.num_autobus);
        setDescripcion(report.descripcion);
        // Scroll to top
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setDescripcion('');
        const today = new Date();
        setFecha(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`);
        setNumAutobus('');
    };

    const handleDelete = (id) => {
        setDeleteTargetId(id);
        setDeleteModalOpen(true);
    };

    const confirmDelete = async () => {
        try {
            await averiasApi.delete(deleteTargetId);
            toast.success('Parte eliminado correctamente');
            loadRecentReports(numAutobus.trim());
        } catch (error) {
            toast.error(error.message || 'Error al eliminar el parte');
        } finally {
            setDeleteModalOpen(false);
            setDeleteTargetId(null);
        }
    };

    // Estado badge component
    const EstadoBadge = ({ estado }) => {
        const styles = {
            pendiente: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
            en_proceso: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
            reparado: 'bg-green-500/20 text-green-400 border-green-500/30'
        };
        const icons = {
            pendiente: <Clock className="w-3 h-3" />,
            en_proceso: <Wrench className="w-3 h-3" />,
            reparado: <CheckCircle className="w-3 h-3" />
        };
        const labels = {
            pendiente: 'Pendiente',
            en_proceso: 'En proceso',
            reparado: 'Reparado'
        };

        return (
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${styles[estado] || styles.pendiente}`}>
                {icons[estado] || icons.pendiente}
                {labels[estado] || 'Pendiente'}
            </span>
        );
    };

    return (
        <div className="space-y-4 pb-24">
            {/* Header */}
            <div className="bg-gradient-to-r from-orange-600 to-amber-600 px-4 py-6 rounded-b-3xl shadow-lg relative mb-4">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/user')}
                        className="bg-white/20 hover:bg-white/30 backdrop-blur p-2 rounded-full transition-colors text-white"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div className="flex-1">
                        <h1 className="text-2xl font-bold text-white">{editingId ? 'Editar Parte' : 'Parte de Avería'}</h1>
                        <p className="text-sm font-medium text-orange-100">{editingId ? 'Modifica los datos del parte' : 'Reporta problemas en el vehículo'}</p>
                    </div>
                </div>
            </div>

            {/* Form Card */}
            <div className="bg-[var(--glass-bg)] border border-[var(--glass-border)] shadow-sm rounded-2xl p-4 mx-4">
                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Fecha */}
                    <div>
                        <label className="flex items-center gap-2 text-sm font-bold text-[var(--text-muted)] mb-2 uppercase tracking-wider">
                            <Calendar className="w-4 h-4 text-orange-500" />
                            Fecha
                        </label>
                        <input
                            type="date"
                            value={fecha}
                            onChange={(e) => setFecha(e.target.value)}
                            className="w-full px-4 py-3 bg-[var(--bg-primary)] border border-[var(--glass-border)] rounded-xl text-[var(--text-primary)] focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition-all font-medium"
                        />
                    </div>

                    {/* Nº Autobús */}
                    <div>
                        <label className="flex items-center gap-2 text-sm font-bold text-[var(--text-muted)] mb-2 uppercase tracking-wider">
                            <Bus className="w-4 h-4 text-orange-500" />
                            Nº de Autobús
                        </label>
                        <input
                            type="text"
                            value={numAutobus}
                            onChange={(e) => setNumAutobus(e.target.value)}
                            placeholder="Ej: 142"
                            className="w-full px-4 py-3 bg-[var(--bg-primary)] border border-[var(--glass-border)] rounded-xl text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition-all font-bold text-lg"
                        />
                    </div>

                    {/* Descripción */}
                    <div>
                        <label className="flex items-center gap-2 text-sm font-bold text-[var(--text-muted)] mb-2 uppercase tracking-wider">
                            <FileText className="w-4 h-4 text-orange-500" />
                            Descripción de la avería
                        </label>
                        <textarea
                            value={descripcion}
                            onChange={(e) => setDescripcion(e.target.value)}
                            placeholder="Describe el problema con el mayor detalle posible..."
                            rows={4}
                            className="w-full px-4 py-3 bg-[var(--bg-primary)] border border-[var(--glass-border)] rounded-xl text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition-all font-medium resize-none shadow-inner"
                        />
                    </div>

                    {/* Submit Button */}
                    <button
                        type="submit"
                        disabled={submitting}
                        className="w-full flex items-center justify-center gap-2 py-3.5 bg-gradient-to-r from-orange-500 to-amber-500 rounded-xl font-bold text-white shadow-lg shadow-orange-500/30 disabled:opacity-50 transition-transform active:scale-95"
                    >
                        {submitting ? (
                            <>
                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                Enviando...
                            </>
                        ) : (
                            <>
                                <Send className="w-5 h-5" />
                                {editingId ? 'Actualizar Parte' : 'Enviar Parte'}
                            </>
                        )}
                    </button>

                    {editingId && (
                        <button
                            type="button"
                            onClick={handleCancelEdit}
                            className="w-full py-3 bg-[var(--glass-bg)] border border-[var(--glass-border)] rounded-xl font-bold text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-all"
                        >
                            Cancelar Edición
                        </button>
                    )}
                </form>
            </div>

            {/* Recent Reports Section */}
            {numAutobus.trim().length >= 2 && (
                <div className="space-y-3 px-4">
                    <h2 className="flex items-center gap-2 text-sm font-bold text-[var(--text-muted)] uppercase tracking-wider mt-6">
                        <AlertCircle className="w-4 h-4 text-orange-500" />
                        Últimos partes aut. {numAutobus}
                    </h2>

                    {loadingRecent ? (
                        <div className="flex items-center justify-center py-8">
                            <div className="w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
                        </div>
                    ) : recentReports.length === 0 ? (
                        <div className="text-center py-6 text-[var(--text-muted)] bg-[var(--glass-bg)] border border-[var(--glass-border)] rounded-xl shadow-sm">
                            <Wrench className="w-8 h-8 mx-auto mb-2 opacity-50" />
                            <p className="text-sm font-medium">No hay partes recientes</p>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {Array.isArray(recentReports) && recentReports.map((report) => (
                                <div
                                    key={report.id}
                                    className="bg-[var(--glass-bg)] rounded-xl p-4 border border-[var(--glass-border)] shadow-sm"
                                >
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="flex-1 min-w-0">
                                            <p className="font-bold text-[var(--text-primary)] line-clamp-2">
                                                {report.descripcion}
                                            </p>
                                            <p className="text-xs font-medium text-[var(--text-muted)] mt-1">
                                                {report.fecha ? format(new Date(report.fecha), 'd MMM yyyy', { locale: es }) : 'Sin fecha'}
                                                {report.conductor_nombre && ` · ${report.conductor_nombre}`}
                                            </p>
                                        </div>
                                        <EstadoBadge estado={report.estado} />
                                    </div>

                                    {/* Edit/Delete Actions - Only for owner and same day (or admin) */}
                                    {(user?.role === 'admin' || (user?.conductorId && report.conductor_id === user.conductorId && (!report.fecha || report.fecha === new Date().toISOString().split('T')[0]))) && (
                                        <div className="flex items-center gap-2 mt-3 p-3 border-t border-[var(--glass-border)] justify-end">
                                            <button
                                                onClick={() => handleEdit(report)}
                                                className="p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--glass-bg)] rounded transition-colors"
                                                title="Editar"
                                            >
                                                <Pencil className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(report.id)}
                                                className="p-1 text-[var(--text-muted)] hover:text-red-500 hover:bg-[var(--glass-bg)] rounded transition-colors"
                                                title="Eliminar"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Confirm Delete Modal */}
            <ConfirmModal
                isOpen={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                onConfirm={confirmDelete}
                title="Eliminar Parte de Avería"
                message="¿Estás seguro de que deseas eliminar este parte? Esta acción no se puede deshacer."
                confirmText="Eliminar"
                cancelText="Cancelar"
                variant="danger"
            />
        </div>
    );
}
